import { GroupOutlined, InfoOutlined, KeyboardArrowDown, StickyNote2Outlined } from '@mui/icons-material'
import {
  Box,
  Checkbox,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import { Fragment } from 'react'
import type { ManufacturerRow } from './ManufacturerList.types'
import { formatUrl, getUniqueAliasEntries } from './helpers'

export interface ManufacturerTableProps {
  rows: ManufacturerRow[]
  expandedById: Record<number, boolean>
  onToggleExpand: (id: number) => void
  onEditManufacturer: (id: number) => void
  onEditAlias: (manufacturerId: number, aliasIndex: number) => void
  onOpenAuthorizedOem: (manufacturer: ManufacturerRow) => void
  onOpenNotes: (rowId: number) => void
}

export function ManufacturerTable({
  rows,
  expandedById,
  onToggleExpand,
  onEditManufacturer,
  onEditAlias,
  onOpenAuthorizedOem,
  onOpenNotes,
}: ManufacturerTableProps) {
  return (
    <Table stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
      <TableHead
        sx={{
          bgcolor: '#3b5685',
          '& th': { color: '#fff', fontWeight: 600, py: 1.5 },
        }}
      >
        <TableRow>
          <TableCell width="22%">Manufacturers</TableCell>
          <TableCell width="20%">Alias</TableCell>
          <TableCell width="16%">OEM Code</TableCell>
          <TableCell width="22%">URL</TableCell>
          <TableCell width="10%">
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              Direct
              <Tooltip title="Direct relationship">
                <InfoOutlined sx={{ fontSize: 16 }} />
              </Tooltip>
            </Box>
          </TableCell>
          <TableCell width="10%">Authorized Suppliers</TableCell>
          <TableCell width="8%">Notes</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row: ManufacturerRow) => {
          const isExpanded = !!expandedById[row.id]

          return (
            <Fragment key={row.id}>
              {/* Parent row */}
              <TableRow hover sx={{ bgcolor: isExpanded ? '#eef1f4' : 'transparent' }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Link component="button" underline="hover" onClick={() => onEditManufacturer(row.id)}>
                      {row.provider}
                    </Link>
                    <IconButton
                      size="small"
                      aria-label={isExpanded ? 'Collapse manufacturer' : 'Expand manufacturer'}
                      onClick={() => onToggleExpand(row.id)}
                    >
                      <KeyboardArrowDown
                        sx={{
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 150ms ease',
                        }}
                      />
                    </IconButton>
                  </Box>
                </TableCell>

                <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                <TableCell>{row.mfrCodeUpper ?? '—'}</TableCell>

                <TableCell>
                  {row.url ? (
                    <Link href={formatUrl(row.url) ?? undefined} target="_blank" rel="noopener noreferrer">
                      {row.url}
                    </Link>
                  ) : (
                    '—'
                  )}
                </TableCell>

                <TableCell>
                  <Checkbox size="small" disabled />
                </TableCell>

                <TableCell>
                  <IconButton onClick={() => onOpenAuthorizedOem(row)}>
                    <GroupOutlined />
                  </IconButton>
                </TableCell>

                <TableCell>
                  <IconButton aria-label="Open notes" onClick={() => onOpenNotes(row.id)}>
                    <StickyNote2Outlined />
                  </IconButton>
                </TableCell>
              </TableRow>
              {isExpanded &&
                getUniqueAliasEntries(row.aliases).map(({ alias, index }) => (
                  <TableRow key={`m:${row.id}|alias:${index}`} hover>
                    <TableCell>
                      <Box sx={{ pl: 3.5 }}>
                        <Link component="button" underline="hover" onClick={() => onEditAlias(row.id, index)}>
                          Link
                        </Link>
                      </Box>
                    </TableCell>

                    <TableCell>{alias}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>

                    <TableCell>
                      <Checkbox size="small" disabled />
                    </TableCell>

                    <TableCell>
                      <IconButton aria-label="Open authorized suppliers" onClick={() => onOpenAuthorizedOem(row)}>
                        <GroupOutlined />
                      </IconButton>
                    </TableCell>

                    <TableCell />
                  </TableRow>
                ))}
            </Fragment>
          )
        })}
      </TableBody>
    </Table>
  )
}
